import React, { useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'

import { enquiryInputType, enquiryStatus, strings } from '@constants'
import { paths } from '@routes'
import { Spacings } from '@styles'
import { getEnquiryInputType, showDateFormat, decimalSeparatedNumber } from '@utils'
import { toggleDrawer, updateDrawerMeta } from '@data/state/action/root'
import { AddressCard, DrawerBodyWrapper, DrawerFooterWrapper, ItemCard } from '@base'
import { Button, Pill } from '@baseV2'

import { SectionTitle } from '../styles'

const Body = styled(DrawerBodyWrapper)`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_4B};
`

const PillsRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: ${Spacings.SPACING_2B};
`

const ItemsList = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_3B};
`

const DateRow = styled.div`
	display: flex;
	justify-content: space-between;
	gap: ${Spacings.SPACING_4B};
`

const DateBlock = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_1B};
`

const DateValue = styled.span`
	font-weight: 600;
	color: ${({ theme }) => theme.colors.text.primary};
`

const FooterActions = styled.div`
	display: flex;
	gap: ${Spacings.SPACING_3B};
`

const ViewEnquiry = ({ enquiry, role }) => {
	const navigate = useNavigate()
	const dispatch = useDispatch()

	const enquiryItems = enquiry?.enquiry_items ?? []

	const sumByType = (type) => enquiryItems?.filter((item) => item?.input_type === type)?.reduce((acc, item) => acc + +item?.required_weight, 0) ?? 0

	const totalWeight =
		enquiryItems
			?.filter((item) => item?.input_type === enquiryInputType.WEIGHT || item?.input_type === null)
			?.reduce((acc, item) => acc + +item?.required_weight, 0) ?? 0
	const totalLength = sumByType(enquiryInputType.LENGTH)
	const totalSet = sumByType(enquiryInputType.SET)
	const totalPieces = sumByType(enquiryInputType.PIECES)
	const totalNumber = sumByType(enquiryInputType.NUMBER)
	const totalPower = sumByType(enquiryInputType.POWER)

	useEffect(() => {
		dispatch(updateDrawerMeta({ label: `${strings('view_enquiry')}  (${strings('enquiry_id')}: ${enquiry?.enquiry_id})` }))
	}, [dispatch, enquiry])

	const scrapPlatform = () => {
		const { final_quote_buyer } = enquiry
		const quotePerProduct = final_quote_buyer?.quote_per_product.find((item) => item?.for_price || item?.ex_price)
		return enquiry?.platform === 'SCRAP' && !!quotePerProduct
	}

	const goEdit = () => {
		dispatch(toggleDrawer(false))
		if ([enquiryStatus.active, enquiryStatus.orderPlaced, enquiryStatus.expired].indexOf(enquiry.enquiry_status) > -1) {
			// if (scrapPlatform()) {
			// 	navigate(`${paths.manage_purchase_order_status('active')}?enquiry_id=${enquiry?.enquiry_id}`)
			// 	return
			// }
			if (enquiry.enquiry_status === enquiryStatus.active && !enquiry.final_quote_buyer) {
				navigate(paths.edit_enquiry(enquiry.enquiry_id), { state: enquiry })
				return
			}
			navigate(paths.admin_manage_enquiry_manage(enquiry.enquiry_id), { state: enquiry })
		} else {
			navigate(paths.edit_enquiry(enquiry.enquiry_id), { state: enquiry })
		}
	}

	const createPoClickHandler = () => navigate(paths.create_po(enquiry.enquiry_id), { state: enquiry })

	const getActionButtonText = () => {
		if (enquiry.enquiry_status === enquiryStatus.expired) return strings(['view', 'quotes'])
		if (enquiry.enquiry_status === enquiryStatus.orderPlaced || enquiry.final_quote_buyer) {
			if (scrapPlatform()) return strings(['previous', 'orders'])
			return strings(['see', 'quotes'])
		}
		return strings('edit')
	}

	const renderItems = () =>
		enquiryItems.map((item, index) => {
			const allotedLabel = getEnquiryInputType(item?.input_type)
			const itemData = [
				{ label: strings('category'), value: item?.category_name },
				{ label: strings('product'), value: item?.product_name ?? '-', wrap: true },
				{
					label: allotedLabel,
					value:
						item?.input_type === enquiryInputType.WEIGHT || item?.input_type === null
							? decimalSeparatedNumber((item?.required_weight / 1000).toFixed(3))
							: item?.required_weight,
				},
				{ label: strings('brands'), value: item.brands?.join(', ') || item?.brand },
				{ label: strings('grade'), value: item?.grade ?? '-' },
				{ label: strings('tc_reqd'), value: item?.tc_required ? strings('yes') : strings('no') },
				{ label: strings('message'), value: item?.message ?? '-', isMessage: true },
			]
			return <ItemCard small data={itemData} id={item?.id} key={`enquiry-item-${index}`} />
		})

	return (
		<>
			<Body>
				<PillsRow>
					<Pill>{`${strings('total_items')}: ${enquiryItems?.length}`}</Pill>
					{totalWeight > 0 && <Pill>{`${strings('weight_mt')}: ${decimalSeparatedNumber(totalWeight / 1000, { default: 0 })}`}</Pill>}
					{totalLength > 0 && <Pill>{`${strings('length')}: ${totalLength}`}</Pill>}
					{totalSet > 0 && <Pill>{`${strings('set')}: ${totalSet}`}</Pill>}
					{totalPieces > 0 && <Pill>{`${strings('pieces')}: ${totalPieces}`}</Pill>}
					{totalNumber > 0 && <Pill>{`${strings('number')}: ${totalNumber}`}</Pill>}
					{totalPower > 0 && <Pill>{`${strings('power')}: ${totalPower}`}</Pill>}
				</PillsRow>

				<ItemsList>{renderItems()}</ItemsList>

				<SectionTitle>{strings('shipping_address')}</SectionTitle>
				<AddressCard data={enquiry?.shipping_address} />

				<DateRow>
					<DateBlock>
						<SectionTitle>{strings('enquiry_date')}</SectionTitle>
						<DateValue>{showDateFormat(new Date(enquiry?.enquiry_date))}</DateValue>
					</DateBlock>
					<DateBlock>
						<SectionTitle>{strings('expiry_date')}</SectionTitle>
						<DateValue>{showDateFormat(new Date(enquiry?.expiry_date))}</DateValue>
					</DateBlock>
				</DateRow>
			</Body>
			<DrawerFooterWrapper>
				<FooterActions>
					<Button color='primary' onClick={goEdit}>
						{getActionButtonText()}
					</Button>
					{enquiry?.platform === 'SCRAP' && enquiry?.enquiry_status !== enquiryStatus.created && (
						<Button variant='outline' color='neutral' onClick={createPoClickHandler}>
							{strings(['create', 'po'])}
						</Button>
					)}
				</FooterActions>
			</DrawerFooterWrapper>
		</>
	)
}

export default ViewEnquiry
